import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Retro terminal template — green CRT monitor aesthetic.
 *
 *   ┌─ PROFILE.SYS ─────────────────[]
 *   │ > user@enahs
 *   │   badges: ★ ★ ★
 *   │ > bio: description...
 *   │ > links/
 *   │   ├─ github
 *   │   └─ twitch
 *   │ > meta: views=23 loc=nowhere
 *   └─────────────────────────────
 *
 * The card itself becomes the terminal window: chrome title bar with traffic
 * lights, monospaced ASCII-style prompts inside. Scan-line overlay +
 * phosphor glow come from CSS. All interactive behavior (links, audio,
 * widgets) still works — this is a visual re-skin, not a simulator.
 */
export function renderRetroTerminal(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--terminal");

  const titleBar = el("div", { class: "terminal__titlebar" }, [
    el("span", { class: "terminal__dot terminal__dot--r" }),
    el("span", { class: "terminal__dot terminal__dot--y" }),
    el("span", { class: "terminal__dot terminal__dot--g" }),
    el("span", { class: "terminal__title" }, [`PROFILE.SYS — ${config.profile.username}`]),
  ]);

  const userLine = el("div", { class: "terminal__line" }, [
    el("span", { class: "terminal__prompt" }, ["> "]),
    el("span", { class: "terminal__cmd" }, ["whoami"]),
  ]);
  const userOut = el("div", { class: "terminal__out" }, [
    p.avatar,
    el("div", { class: "terminal__id" }, [
      el("div", { class: "card__name-row" }, [p.username, p.badges]),
      p.description,
    ]),
  ]);

  const bioLine = el("div", { class: "terminal__line" }, [
    el("span", { class: "terminal__prompt" }, ["> "]),
    el("span", { class: "terminal__cmd" }, ["ls links/"]),
  ]);
  const linksOut = el("div", { class: "terminal__out" });
  if (p.socials) linksOut.append(p.socials);
  if (p.links) linksOut.append(p.links);

  const metaRow = el("div", { class: "card__meta terminal__meta" });
  const vc = p.meta.getInline("views");    if (vc) metaRow.append(vc);
  const loc = p.meta.getInline("location"); if (loc) metaRow.append(loc);
  const clk = p.meta.getInline("clock");    if (clk) metaRow.append(clk);
  const ct = p.meta.getInline("contact");   if (ct) metaRow.append(ct);
  const metaLine = el("div", { class: "terminal__line" }, [
    el("span", { class: "terminal__prompt" }, ["> "]),
    el("span", { class: "terminal__cmd" }, ["cat status"]),
  ]);

  const body = el("div", { class: "terminal__body" }, [
    userLine, userOut,
    bioLine, linksOut,
  ]);
  if (p.widgets) {
    body.append(el("div", { class: "terminal__line" }, [
      el("span", { class: "terminal__prompt" }, ["> "]),
      el("span", { class: "terminal__cmd" }, ["ps aux"]),
    ]));
    body.append(el("div", { class: "terminal__out" }, [p.widgets]));
  }
  if (p.audio) {
    body.append(el("div", { class: "terminal__line" }, [
      el("span", { class: "terminal__prompt" }, ["> "]),
      el("span", { class: "terminal__cmd" }, ["play --loop"]),
    ]));
    body.append(el("div", { class: "terminal__out" }, [p.audio]));
  }
  if (metaRow.childElementCount > 0) {
    body.append(metaLine);
    body.append(el("div", { class: "terminal__out terminal__out--inline" }, [metaRow]));
  }

  p.card.append(titleBar, body);
  p.meta.applyCornerZones();
  return p.card;
}
